import React from 'react';
import { ArrowRight, ClipboardList, FileText, ShieldCheck, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

const capabilities = [
  { icon: ClipboardList, title: 'Coordinate every work order', text: 'Create, assign, prioritize, and follow each job from request through closeout.' },
  { icon: Users, title: 'Keep customer details connected', text: 'Bring locations, contacts, service history, and job notes into one clear record.' },
  { icon: FileText, title: 'Move smoothly into invoicing', text: 'Turn completed work into professional invoices and see what still needs action.' },
  { icon: ShieldCheck, title: 'Stay ahead of compliance', text: 'Track vendor documents, coverage requirements, and renewal dates with confidence.' },
];

const LandingPage: React.FC = () => (
  <div className="bg-white text-slate-900">
    <section className="border-b border-slate-200 bg-slate-50">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-16 sm:px-8 md:py-24 lg:grid-cols-[.9fr_1.1fr]">
        <div>
          <p className="mb-5 text-xs font-bold uppercase tracking-[.17em] text-blue-700">Work order management, simplified</p>
          <h1 className="max-w-xl text-4xl font-semibold leading-[1.07] tracking-[-.04em] sm:text-5xl lg:text-6xl">The work gets easier when everything is connected.</h1>
          <p className="mt-7 max-w-lg text-lg leading-relaxed text-slate-600">WorkOrder HQ gives your team one reliable place for jobs, people, service history, and invoices—so everyone knows what needs to happen next.</p>
          <div className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center">
            <Link to="/signup" className="inline-flex items-center justify-center gap-2 rounded bg-blue-600 px-5 py-3.5 text-sm font-bold text-white transition-colors hover:bg-blue-700">Start your free trial <ArrowRight className="h-4 w-4" /></Link>
            <a href="#platform" className="inline-flex items-center justify-center gap-2 px-3 py-3 text-sm font-semibold text-slate-800">See the platform <ArrowRight className="h-4 w-4" /></a>
          </div>
          <p className="mt-5 text-xs text-slate-500">No credit card required · Set up in minutes</p>
        </div>
        <div className="relative">
          <div className="absolute -right-5 -top-6 h-32 w-32 rounded-full bg-blue-100"></div>
          <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_28px_65px_-32px_rgba(15,23,42,.35)]">
            <img src="/workorder-dashboard-preview.png" alt="WorkOrder HQ dashboard showing active work orders" className="w-full" />
          </div>
        </div>
      </div>
    </section>

    <section id="platform" className="py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-8 border-b border-slate-200 pb-12 lg:grid-cols-[.65fr_1fr]">
          <p className="text-xs font-bold uppercase tracking-[.17em] text-blue-700">One clear operating picture</p>
          <div><h2 className="max-w-3xl text-3xl font-semibold leading-tight tracking-[-.03em] sm:text-4xl">A practical system for the everyday work behind great service.</h2><p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-600">The product stays front and center: clear information, consistent processes, and fewer details slipping through the cracks.</p></div>
        </div>
        <div className="grid gap-x-12 gap-y-10 pt-12 md:grid-cols-2">
          {capabilities.map(({ icon: Icon, title, text }) => <article key={title} className="grid grid-cols-[42px_1fr] gap-4 border-t border-slate-200 pt-5"><span className="flex h-9 w-9 items-center justify-center rounded bg-blue-50 text-blue-700"><Icon className="h-4 w-4" /></span><div><h3 className="mt-1 text-xl font-semibold tracking-tight">{title}</h3><p className="mt-2 max-w-md leading-relaxed text-slate-600">{text}</p></div></article>)}
        </div>
      </div>
    </section>

    <section className="border-y border-slate-200 bg-slate-50 py-20">
      <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
        <p className="text-xs font-bold uppercase tracking-[.17em] text-blue-700">Ready when you are</p>
        <h2 className="mx-auto mt-5 max-w-2xl text-3xl font-semibold tracking-[-.03em] sm:text-4xl">Get the work out of your head and into a system your team can trust.</h2>
        <Link to="/signup" className="mt-8 inline-flex items-center gap-2 rounded bg-blue-600 px-5 py-3.5 text-sm font-bold text-white transition-colors hover:bg-blue-700">Start your free trial <ArrowRight className="h-4 w-4" /></Link>
      </div>
    </section>
  </div>
);

export default LandingPage;
